import { Trans, t } from '@lingui/macro';
import { Link } from 'react-router-dom';
import { Calendar, Globe, MapPin, Building } from 'lucide-react';

import { Card, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';

export default function EventHeader(props) {
    const {
        title,
        parentMode,
        eventId,
        hostName,
        hostId,
        ticketsAvailable,
        seatPreviewAvailable,
        soldOut,
        chargesEnabled,
        checkoutLabel,
        onGetTickets,
        onPreviewSeatmap,
        date,
        venue,
        address,
        mapsURL,
        description,
        descriptionHtml,
        imageUrl,
        websiteUrl,
        facebookUrl,
        twitterUrl,
        instagramUrl,
        tiktokUrl,
        linkedinUrl,
        youtubeUrl,
    } = props;
    const checkoutDisabled =
        soldOut || chargesEnabled === 0 || (!ticketsAvailable && !seatPreviewAvailable);

    return (
        <div className="max-w-screen-lg mx-auto p-4">
            <Card className="overflow-hidden flex flex-col border-solid border-border shadow-none">
                {imageUrl ? (
                    <div className="w-full aspect-[2/1] overflow-hidden bg-muted/30">
                        <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                    </div>
                ) : null}

                <div className="flex flex-col flex-1">
                    <div className="flex flex-col flex-1 p-4">
                        <div className="mb-4">
                            {parentMode && (
                                <p className="text-sm font-medium text-primary mb-2">
                                    <Trans>Season</Trans>
                                </p>
                            )}
                            <h1 className="text-lg font-bold font-inter text-foreground mb-2 mt-0">
                                {title}
                            </h1>
                            <Link
                                to={`/host/${hostId}`}
                                className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
                            >
                                {hostName}
                            </Link>
                        </div>

                        <div className="flex gap-2 items-center mb-2">
                            <Calendar
                                size={18}
                                className="text-muted-foreground shrink-0"
                                aria-hidden
                            />
                            <span className="text-sm">{date}</span>
                            <Link
                                to={`/event/${eventId}/calendar`}
                                className="flex text-xs text-primary hover:opacity-60 transition-opacity"
                            >
                                <Trans>Add to Calendar</Trans>
                            </Link>
                        </div>

                        {venue ? (
                            <div className="flex gap-2 items-center mb-2">
                                <Building
                                    size={18}
                                    className="text-muted-foreground shrink-0 mt-0.5"
                                    aria-hidden
                                />
                                <span className="text-sm text-muted-foreground">{venue}</span>
                            </div>
                        ) : null}

                        <div className="flex gap-2 items-center mb-2">
                            <MapPin
                                size={18}
                                className="text-muted-foreground shrink-0 mt-0.5"
                                aria-hidden
                            />
                            <span className="text-sm text-muted-foreground">{address}</span>
                            {mapsURL ? (
                                <Link
                                    to={mapsURL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex text-xs text-primary hover:opacity-60 transition-opacity"
                                >
                                    <Trans>View Map</Trans>
                                </Link>
                            ) : null}
                        </div>

                        <div className="mt-4">
                            <SocialLinks
                                websiteUrl={websiteUrl}
                                facebookUrl={facebookUrl}
                                twitterUrl={twitterUrl}
                                instagramUrl={instagramUrl}
                                tiktokUrl={tiktokUrl}
                                linkedinUrl={linkedinUrl}
                                youtubeUrl={youtubeUrl}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end px-4 pb-3 border-t border-border mt-auto">
                        <Button
                            className="h-[46px] px-6 font-bold uppercase tracking-wide rounded-md"
                            onClick={seatPreviewAvailable ? onPreviewSeatmap : onGetTickets}
                            disabled={checkoutDisabled}
                            variant={checkoutDisabled ? 'outline' : 'default'}
                        >
                            {checkoutLabel}
                        </Button>
                    </div>
                </div>
            </Card>

            <h2 className="mt-12 text-3xl font-extrabold font-inter text-foreground">
                <Trans>About</Trans>
            </h2>

            <Card className="my-8 border-solid border-border shadow-none">
                <CardContent className="p-4">
                    {descriptionHtml ? (
                        <div
                            className={descriptionProseClass}
                            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                        />
                    ) : (
                        <p className={cn(descriptionProseClass, 'whitespace-pre-wrap')}>
                            {description}
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

const descriptionProseClass =
    'text-sm text-foreground leading-relaxed [&_p]:mt-0 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_p:empty]:block [&_p:empty]:h-6 [&_p:empty]:mb-0 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-primary/90';

function SocialLinks(props) {
    const {
        websiteUrl,
        facebookUrl,
        twitterUrl,
        instagramUrl,
        tiktokUrl,
        linkedinUrl,
        youtubeUrl,
    } = props;

    const socialLinksAvailable = Boolean(
        websiteUrl ||
            facebookUrl ||
            twitterUrl ||
            instagramUrl ||
            tiktokUrl ||
            linkedinUrl ||
            youtubeUrl,
    );

    if (!socialLinksAvailable) {
        return null;
    }

    const iconBtn =
        'inline-flex cursor-pointer items-center justify-center h-10 w-10 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/40 transition-colors';

    return (
        <div className="flex flex-wrap gap-1">
            {websiteUrl && (
                <a
                    aria-label={t`Website`}
                    href={websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={iconBtn}
                >
                    <Globe className="h-[22px] w-[22px]" strokeWidth={2} aria-hidden />
                </a>
            )}
            {facebookUrl && (
                <a
                    aria-label="Facebook"
                    href={facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={iconBtn}
                >
                    <IconFacebook className="h-[22px] w-[22px]" />
                </a>
            )}
            {instagramUrl && (
                <a
                    aria-label="Instagram"
                    href={instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={iconBtn}
                >
                    <IconInstagram className="h-[22px] w-[22px]" />
                </a>
            )}
            {twitterUrl && (
                <a
                    aria-label="X"
                    href={twitterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={iconBtn}
                >
                    <img
                        alt=""
                        src={process.env.REACT_APP_STOREFRONT_S3_LOCATION + '/web/x-icon-black.svg'}
                        className="w-5 h-5 invert opacity-90"
                    />
                </a>
            )}
            {tiktokUrl && (
                <a
                    aria-label="Tiktok"
                    href={tiktokUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={iconBtn}
                >
                    <img
                        alt=""
                        src={
                            process.env.REACT_APP_STOREFRONT_S3_LOCATION +
                            '/web/tiktok-icon-black.svg'
                        }
                        className="w-5 h-5 invert opacity-90"
                    />
                </a>
            )}
            {linkedinUrl && (
                <a
                    aria-label="Linkedin"
                    href={linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={iconBtn}
                >
                    <IconLinkedIn className="h-[22px] w-[22px]" />
                </a>
            )}
            {youtubeUrl && (
                <a
                    aria-label="YouTube"
                    href={youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={iconBtn}
                >
                    <IconYouTube className="h-[22px] w-[22px]" />
                </a>
            )}
        </div>
    );
}

function IconFacebook({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
    );
}

function IconInstagram({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 4.782-2.618 6.98-6.98.058-1.28.072-1.689.072-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.98-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
        </svg>
    );
}

function IconLinkedIn({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
    );
}

function IconYouTube({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
    );
}
